import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import Pagination from '@/components/base/Pagination';
import MediaCard from '@/components/feature/MediaCard';
import TagFilter from '@/components/feature/TagFilter';
import { mediaList } from '@/mocks/media';
import { categoryMeta, genreOptions, regionOptions, sortOptions, yearOptions } from '@/mocks/filters';
import { filterMedia, mediaByType, sortMedia, type SortKey } from '@/utils/media';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import type { MediaType } from '@/types/media';

const PAGE_SIZE = 4;
const VALID_TYPES: MediaType[] = ['movie', 'tv', 'variety'];

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const meta = categoryMeta[(type as MediaType) ?? 'movie'];
  const validType = VALID_TYPES.includes(type as MediaType);

  const [genre, setGenre] = useState('全部');
  const [region, setRegion] = useState('全部');
  const [year, setYear] = useState('全部');
  const [sort, setSort] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);

  useDocumentTitle(
    validType ? `${meta.title} - 蘑菇视频` : '分类未找到 - 蘑菇视频',
    validType ? meta.desc : '未找到对应的影视分类。',
  );

  const results = useMemo(() => {
    if (!validType) return [];
    const base = mediaByType(mediaList, type as MediaType);
    const filtered = filterMedia(base, { genre, region, year, rating: '全部' });
    return sortMedia(filtered, sort);
  }, [type, validType, genre, region, year, sort]);

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const update = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  const resetFilters = () => {
    setGenre('全部');
    setRegion('全部');
    setYear('全部');
    setSort('latest');
    setPage(1);
  };

  if (!validType) {
    return (
      <div className="w-full max-w-content mx-auto px-4 md:px-8 py-16">
        <EmptyState
          title="未找到该分类"
          description="你访问的分类不存在，返回首页看看其他精彩内容吧。"
          icon="ri-error-warning-line"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-content mx-auto px-4 md:px-8 py-6">
      <Breadcrumb items={[{ label: meta.name }]} />

      <section className="relative mt-4 rounded-lg overflow-hidden">
        <div className="absolute inset-0">
          <img src={meta.cover} alt={`${meta.title} - 蘑菇视频`} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary-950/90 via-secondary-950/70 to-secondary-950/40" />
        </div>
        <div className="relative px-5 py-8 md:px-10 md:py-12">
          <h1 className="text-2xl md:text-3xl font-black text-background-50 mb-2">{meta.title}</h1>
          <p className="text-sm md:text-base text-background-200/90 max-w-2xl">{meta.desc}</p>
        </div>
      </section>

      <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex flex-col gap-4">
          <TagFilter label="类型" options={genreOptions} value={genre} onChange={update(setGenre)} />
          <TagFilter label="地区" options={regionOptions} value={region} onChange={update(setRegion)} />
          <TagFilter label="年份" options={yearOptions} value={year} onChange={update(setYear)} />
        </div>
        <div className="mt-4 pt-4 border-t border-background-200 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-sm text-foreground-500">
            共 <strong className="text-primary-600 font-semibold">{results.length}</strong> 部内容
          </span>
          <Link
            to={`/category/${type}/filter`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-equalizer-line text-base"></i>
            高级筛选
          </Link>
        </div>
      </section>

      <section className="mt-6">
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <h2 className="text-base md:text-lg font-bold text-foreground-950">{meta.name}列表</h2>
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => {
                  setSort(option.key as SortKey);
                  setPage(1);
                }}
                className={`px-3 py-1 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  sort === option.key
                    ? 'bg-background-50 text-primary-600 font-medium shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {pageItems.length === 0 ? (
          <EmptyState
            title="暂无相关内容"
            description="当前筛选条件下没有找到匹配的影视内容，试试重置筛选条件。"
            icon="ri-search-eye-line"
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {pageItems.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </section>
    </div>
  );
}